/**
 * A boarding pass, photographed, becomes the trip (6.1, Discovery §12 "the
 * capture is the record"): the brain reads the flight — its day and the two
 * airports' countries, never the passenger's name — and the record turns it
 * into the trip it is: leaving the title's country is a departure; reaching
 * it is the return of the trip the person is on (or of one whose departure
 * they have still to say); a flight that neither leaves nor reaches the
 * country is not a trip of this record. The person confirms the trip, and
 * the photo is kept as its proof — one action.
 *
 * The read is a proposal: every field is the person's to correct before it is
 * written, and nothing is written without them.
 */
import { DOMAIN_ERROR_CODES, apiErrorCode } from '@tonnie/foundation';

import { dateOf, dayOf, type IsoDate } from './dates';

/** The evidence item a boarding pass is kept as — every country's list names it (its config spec). */
export const INOUT_BOARDING_PASS_ITEM = 'boarding_pass';

/** The largest photo or PDF of a boarding pass that is read. */
export const INOUT_BOARDING_PASS_MAX_FILE_BYTES = 15 * 1024 * 1024;

/** Why a boarding pass could not be read — every refusal carries exactly one. */
export const INOUT_BOARDING_PASS_FAILURE_REASONS = [
  /** The file could not be fetched from storage. */
  'storage_fetch_failed',
  /** A file over INOUT_BOARDING_PASS_MAX_FILE_BYTES. */
  'document_too_large',
  /** Not an image, HEIC or PDF (by its bytes). */
  'unsupported_document_type',
  /** A file its parser could not open, or no page in it to read. */
  'document_unreadable',
  /** The model read the file and it is not a boarding pass. */
  'not_a_boarding_pass',
  /** It is a boarding pass, but its day or its airports could not be read. */
  'flight_unreadable',
  /** The model's answer was not the JSON asked for. */
  'invalid_model_response',
  /** The provider refused the request. */
  'provider_rejected',
  /** The provider could not be reached or kept failing. */
  'provider_unavailable',
  /** A defect here — reported, never shown as the person's fault. */
  'internal_error',
] as const;
export type InOutBoardingPassFailureReason = (typeof INOUT_BOARDING_PASS_FAILURE_REASONS)[number];

export function isInOutBoardingPassFailureReason(
  value: unknown
): value is InOutBoardingPassFailureReason {
  return (INOUT_BOARDING_PASS_FAILURE_REASONS as readonly unknown[]).includes(value);
}

/** What a refusal means to the person: a clearer photo, another file, or later. */
export type InOutBoardingPassFailureKind =
  'notBoardingPass' | 'unreadable' | 'unsupported' | 'tooLarge' | 'unavailable' | 'generic';

export const INOUT_BOARDING_PASS_FAILURE_KIND: Record<
  InOutBoardingPassFailureReason,
  InOutBoardingPassFailureKind
> = {
  not_a_boarding_pass: 'notBoardingPass',
  document_unreadable: 'unreadable',
  flight_unreadable: 'unreadable',
  invalid_model_response: 'unreadable',
  unsupported_document_type: 'unsupported',
  document_too_large: 'tooLarge',
  provider_unavailable: 'unavailable',
  provider_rejected: 'unavailable',
  storage_fetch_failed: 'generic',
  internal_error: 'generic',
};

/**
 * A failed read, as the apps receive it, → what to tell the person. The
 * server's `reason` rides on the domain error's `data`; the AI service not
 * answering at all (`AI_UNAVAILABLE`) is `unavailable` — later, not their
 * photo; any other failure (network, 5xx) is `generic`. Never the message.
 */
export function inoutBoardingPassFailure(error: unknown): InOutBoardingPassFailureKind {
  const code = apiErrorCode(error);
  if (code === DOMAIN_ERROR_CODES.AI_UNAVAILABLE) return 'unavailable';
  if (code !== DOMAIN_ERROR_CODES.INOUT_BOARDING_PASS_NOT_READ) return 'generic';
  const reason = (error as { data?: { reason?: unknown } } | null)?.data?.reason;
  return isInOutBoardingPassFailureReason(reason)
    ? INOUT_BOARDING_PASS_FAILURE_KIND[reason]
    : 'generic';
}

/** The flight a boarding pass is for: its day and the countries of its two airports (ISO alpha-2). */
export interface InOutBoardingPassFlight {
  flewOn: IsoDate;
  fromCountry: string;
  toCountry: string;
}

/** The brain's read of a boarding pass. */
export type InOutBoardingPassRead =
  | { success: true; flight: InOutBoardingPassFlight }
  | { success: false; reason: InOutBoardingPassFailureReason };

/**
 * The day of the flight. A boarding pass prints the day and the month, and
 * often no year: the year is the one that puts the flight nearest today — a
 * photo is taken of a pass just used or about to be.
 */
export function inoutBoardingPassDay(
  printed: { year: number | null; month: number; day: number },
  today: IsoDate
): IsoDate | null {
  const valid = (year: number) => {
    const date = new Date(Date.UTC(year, printed.month - 1, printed.day));
    return date.getUTCMonth() === printed.month - 1 && date.getUTCDate() === printed.day
      ? dateOf(date.getTime() / 86_400_000)
      : null;
  };
  if (printed.year !== null) return valid(printed.year);
  const now = dayOf(today);
  const thisYear = new Date(now * 86_400_000).getUTCFullYear();
  const candidates = [thisYear - 1, thisYear, thisYear + 1]
    .map(valid)
    .filter((date): date is IsoDate => date !== null);
  return (
    candidates.reduce<IsoDate | null>(
      (nearest, date) =>
        nearest === null || Math.abs(dayOf(date) - now) < Math.abs(dayOf(nearest) - now)
          ? date
          : nearest,
      null
    ) ?? null
  );
}

/** The trip a flight is, on this record. */
export type InOutBoardingPassTrip =
  /** Leaving the title's country: a new trip, from this day, to where the flight goes. */
  | { kind: 'departure'; departedOn: IsoDate; destinationCountry: string }
  /** Reaching it while away: the return of the trip the person is on. */
  | { kind: 'return'; absenceId: string; returnedOn: IsoDate }
  /** Reaching it with no trip open: a trip back on this day, its departure the person's to say. */
  | { kind: 'returnOnly'; returnedOn: IsoDate; fromCountry: string }
  /** Neither leaving nor reaching the country: not a trip of this record. */
  | { kind: 'elsewhere'; flight: InOutBoardingPassFlight };

export function inoutTripFromBoardingPass(
  flight: InOutBoardingPassFlight,
  record: {
    /** The title's country (ISO alpha-2). */
    hostCountry: string;
    /** The trip the person is on — departed, not returned — or null. */
    openAbsence: { id: string; departedOn: IsoDate } | null;
  }
): InOutBoardingPassTrip {
  const leaves = flight.fromCountry === record.hostCountry;
  const reaches = flight.toCountry === record.hostCountry;
  if (leaves && !reaches) {
    return { kind: 'departure', departedOn: flight.flewOn, destinationCountry: flight.toCountry };
  }
  if (reaches && !leaves) {
    return record.openAbsence && record.openAbsence.departedOn <= flight.flewOn
      ? { kind: 'return', absenceId: record.openAbsence.id, returnedOn: flight.flewOn }
      : { kind: 'returnOnly', returnedOn: flight.flewOn, fromCountry: flight.fromCountry };
  }
  return { kind: 'elsewhere', flight };
}
